import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhoCanJoinSection from '../components/WhoCanJoinSection';
import { GraduationCap, Users, TrendingUp, CheckCircle, Target, Award } from 'lucide-react';

const WhoWeHelpPage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="bg-indigo-100 text-indigo-700 px-6 py-2 rounded-full text-sm font-semibold">
              WHO WE HELP
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            We Transform Ambitious Students
            <br className="hidden md:block" />
            Into IT Professionals
          </h1>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
            CareerLift is designed for Australian students and professionals ready to launch their IT careers
          </p>
        </div>
      </section>

      {/* Who We Help Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-20">
            <div className="text-center">
              <div className="w-32 h-32 bg-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <GraduationCap className="w-16 h-16 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">University Students</h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Enhance your degree with practical IT skills that employers actually want. Bridge the gap between theory and real-world application.
              </p>
              <ul className="mt-6 space-y-3 text-left">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Flexible study schedules that fit around classes</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Build job-ready portfolio while studying</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Secure internships and graduate employed</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Network with industry professionals</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <div className="w-32 h-32 bg-indigo-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Users className="w-16 h-16 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Career Changers</h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Transition from any background into a thriving IT career. No prior experience needed—just ambition and commitment.
              </p>
              <ul className="mt-6 space-y-3 text-left">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Start from basics, no tech background required</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Personalized career counseling included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Job placement and resume support</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Access to Australian job market insights</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <div className="w-32 h-32 bg-green-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <TrendingUp className="w-16 h-16 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Recent Graduates</h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Bridge the gap between graduation and your first IT job. Gain the practical skills employers demand.
              </p>
              <ul className="mt-6 space-y-3 text-left">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Industry-recognized certifications</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Comprehensive interview preparation</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Fast-track to first job in 4-6 months</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Build professional network in tech</span>
                </li>
              </ul>
            </div>
          </div>

          {/* What Makes Us Different */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-center text-slate-900 mb-12">What Makes CareerLift Different?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 border border-indigo-100">
                <Target className="w-12 h-12 text-indigo-600 mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Personalized Learning Path</h3>
                <p className="text-slate-700 leading-relaxed">We don't believe in one-size-fits-all. Every student gets a customized learning path based on their background, goals, and learning pace. Your success is our priority.</p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 border border-indigo-100">
                <Users className="w-12 h-12 text-indigo-600 mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Live Mentor Support</h3>
                <p className="text-slate-700 leading-relaxed">Get direct access to industry professionals who've been where you are. Weekly live sessions, code reviews, and 24/7 AI mentor support ensure you're never stuck.</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-100">
                <Award className="w-12 h-12 text-green-600 mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Practical Portfolio Projects</h3>
                <p className="text-slate-700 leading-relaxed">Build real projects that go straight into your portfolio. From AI chatbots to cloud infrastructure, you'll have tangible proof of your skills for employers.</p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 border border-amber-100">
                <TrendingUp className="w-12 h-12 text-amber-600 mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Job Market Focus</h3>
                <p className="text-slate-700 leading-relaxed">We focus exclusively on skills that Australian employers are hiring for RIGHT NOW. No outdated curriculum—just current, in-demand technologies.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhoCanJoinSection />
      
      {/* CTA Section */}
      <section className="py-20 bg-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Career?</h2>
          <p className="text-xl text-indigo-100 mb-8">Join hundreds of students who've already launched successful IT careers with CareerLift</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => window.location.href = '/contact'} className="bg-white text-indigo-700 font-semibold px-10 py-4 rounded-md hover:bg-gray-50 transition-colors duration-300">Get Started Today</button>
            <button onClick={() => window.location.href = '/programs'} className="bg-transparent text-white font-semibold px-10 py-4 rounded-md border-2 border-white hover:bg-white/10 transition-colors duration-300">View Programs</button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default WhoWeHelpPage;
